import React, { useCallback, useState } from 'react';

import Spinner from 'react-bootstrap/Spinner';
import moment from 'moment';
import WarningAlertIcon from '../../../../images/warning_alert_outline_primary.svg';
import { Colors } from '../../../../themes/colors';
import { useChallengeForm } from '../hooks/useChallengeForm';

import { DateRangePicker } from '../../../commonComponents/DateRangePicker';
import StatePicker, { states as StateOptions } from '../../../commonComponents/StatePicker';
import MultiSelect from '../../../commonComponents/MultiSelect';
import { UserProfession } from '../../../../enums/UserProfession';
import { challengeType, formatDateRange } from '../utils';

import 'react-day-picker/lib/style.css';
import CustomPortal from '../../../commonComponents/CustomPortal';
import { formatNumber } from '../../../../utils/NumberUtils';

export const ProperksChallengeForm = () => {
  const { isDisabled, isLoading, formData, handleChange, handleSubmit } = useChallengeForm();
  const { name, count, type, reward, professionType, levels, states, dateRange } = formData;

  const [isShowLaunchModal, setIsShowLaunchModal] = useState(false);

  const renderSubmitChallengePopup = useCallback(() => {
    if (isShowLaunchModal) {
      return (
        <CustomPortal
          Icon={WarningAlertIcon}
          title="Review Challenge"
          rightButtonProps={{
            color: Colors.primary_500,
          }}
          submitText="Confirm"
          cancelText="Go Back"
          submitPressed={() => {
            handleSubmit();
            setIsShowLaunchModal(false);
          }}
          cancelPressed={() => {
            setIsShowLaunchModal(false);
          }}
        >
          <div
            style={{ display: 'flex', flexDirection: 'column', width: 325, marginBottom: 30 }}
            className="global_font f-dark"
          >
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <b>Challenge Name: </b>
              <p>{name}</p>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <b>Challenge: </b>
              <p>{`${formatNumber(count)} ${challengeType?.[type]}`}</p>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <b>Reward: </b>
              <p>{formatNumber(reward)} ProPoints</p>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <b>Date Range: </b>
              <p>
                {formatDateRange({
                  date_start: moment(dateRange?.from).format('YYYY-MM-DD'),
                  date_end: moment(dateRange?.to).format('YYYY-MM-DD'),
                })}
              </p>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <b>Profession Type: </b>
              <p style={{ width: 200, textAlign: 'right' }}>
                {professionType.length === 4 ? 'All' : professionType?.join?.(', ') || '-'}
              </p>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <b>ProPerks Level: </b>
              <p style={{ width: 200, textAlign: 'right' }}>
                {levels.length === 4 ? 'All' : levels?.join?.(', ') || '-'}
              </p>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <b>Home State: </b>
              <p style={{ width: 200, textAlign: 'right' }}>
                {states.length === StateOptions.length ? 'All' : states?.join?.(', ') || '-'}
              </p>
            </div>
          </div>
        </CustomPortal>
      );
    }

    return null;
  }, [count, dateRange?.from, dateRange?.to, handleSubmit, name, professionType, reward, type]);

  return (
    <div className="card mt-3 ml-3 mr-4 mb-3" style={styles.container}>
      <h1 className="global_font f-dark" style={styles.title}>
        Challenge Creation Form
      </h1>

      <div
        style={{
          width: '90%',
          gap: 30,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <div
          style={{
            display: 'flex',
            gap: 70,
          }}
        >
          {/* Challenge Name Input */}
          <div style={styles.formContainer}>
            <span style={{ fontWeight: 'bold' }}>Challenge Name:</span>
            <input
              className="form-control"
              style={styles.inputStyle}
              placeholder="Enter Name"
              value={name}
              onChange={(e) => handleChange('name', e.target.value)}
            />
          </div>

          {/* Challenge Type Input */}
          <div style={styles.formContainer}>
            <span style={{ fontWeight: 'bold' }}>Challenge:</span>
            <div style={styles.formWrapper}>
              <input
                className="form-control"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="-"
                style={{ ...styles.inputStyle, width: 72 }}
                value={count || ''}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '');
                  handleChange('count', value);
                }}
              />

              <select
                className="custom-select custom-select-lg"
                style={{ ...styles.inputStyle, width: 154 }}
                value={type}
                onChange={(e) => handleChange('type', e.target.value)}
              >
                <option value="" disabled>
                  Select Challenge
                </option>
                <option value="job_completed">{challengeType.job_completed}</option>
                <option value="referral_completed">{challengeType.referral_completed}</option>
                <option value="application_submitted">{challengeType.application_submitted}</option>
              </select>
            </div>
          </div>

          {/* Reward Input */}
          <div style={styles.formContainer}>
            <span style={{ fontWeight: 'bold' }}>Reward:</span>
            <div style={styles.formWrapper}>
              <input
                className="form-control"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="-"
                style={{ ...styles.inputStyle, width: 72 }}
                value={reward || ''}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '');
                  handleChange('reward', value);
                }}
              />
              <span>ProPoints</span>
            </div>
          </div>
          {/* Date Range Input */}
          <DateRangePicker
            label="Date Range"
            placeholder="Select Date Range"
            value={dateRange}
            onChange={(value) => handleChange('dateRange', value)}
          />
        </div>
        <div
          style={{
            display: 'flex',
            gap: 70,
          }}
        >
          {/* Profession Type Input */}
          <div style={styles.formContainer}>
            <span style={{ fontWeight: 'bold' }}>Profession Type:</span>
            <MultiSelect
              className="custom-select custom-select-lg mb-3"
              style={{ position: 'relative', width: 154 }}
              placeholder="Select Profession"
              options={[
                {
                  label: UserProfession.RDH,
                  value: UserProfession.RDH,
                },
                {
                  label: UserProfession.DA,
                  value: UserProfession.DA,
                },
                {
                  label: UserProfession.DN,
                  value: UserProfession.DN,
                },
                {
                  label: UserProfession.FD,
                  value: UserProfession.FD,
                },
              ]}
              autoApply={false}
              showAllButton
              maxSelection={4}
              value={professionType}
              onChange={(values) => handleChange('professionType', values)}
            />
          </div>

          {/* ProPerks Level Input */}
          <div style={styles.formContainer}>
            <span style={{ fontWeight: 'bold' }}>ProPerks Level:</span>
            <MultiSelect
              className="custom-select custom-select-lg mb-3"
              style={{ position: 'relative', width: 154 }}
              placeholder="Select Level"
              options={[
                {
                  label: 'Base Level',
                  value: 0,
                },
                {
                  label: 'Level 1',
                  value: 1,
                },
                {
                  label: 'Level 2',
                  value: 2,
                },
                {
                  label: 'Level 3',
                  value: 3,
                },
              ]}
              autoApply={false}
              showAllButton
              maxSelection={4}
              value={levels}
              onChange={(values) => handleChange('levels', values)}
            />
          </div>

          {/* Home State Input */}
          <div style={styles.formContainer}>
            <span style={{ fontWeight: 'bold' }}>Home State:</span>
            <StatePicker
              multiselect
              style={{
                height: 30,
                width: 154,
              }}
              placeholder="Select State"
              value={states}
              didSelect={(value) => handleChange('states', value)}
            />
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'flex-end',
              width: 200,
              marginBottom: 10,
              paddingRight: 45,
            }}
          >
            <button
              disabled={isLoading || isDisabled}
              onClick={() => setIsShowLaunchModal(true)}
              className="btn btn-success"
              type="button"
            >
              <div style={styles.formWrapper}>
                {isLoading && (
                  <Spinner
                    animation="border"
                    role="status"
                    variant="light"
                    style={{
                      height: 15,
                      width: 15,
                    }}
                  />
                )}
                <span>Launch Challenge</span>
              </div>
            </button>
          </div>
        </div>
      </div>
      {renderSubmitChallengePopup()}
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '85%',
    padding: '0px 35px',
    backgroundColor: Colors.white,
    paddingTop: 45,
    paddingBottom: 45,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 32,
    textAlign: 'center',
  },
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 5,
    width: 200,
  },
  formWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
  },
  inputStyle: {
    width: 200,
    height: 30,
    boxShadow: 'none',
    borderRadius: 2,
    borderColor: Colors.border,
  },
};
